import React, { useState, useEffect, useRef } from "react";
import styles from "../../../styles/IntervalQuiz.module.css";
import { Note } from "../../../soundLibrary";
import { PlaybackOptions } from "../../../components/PlaybackOptionsPanel";
import LearnMoreBanner from "../../../components/LearnMoreBanner";
import { ROUTES } from "../../../config/routes";

export type IntervalQuizOptions = Omit<IntervalQuizProps, "playbackOptions" | "currentQuestion" | "setCurrentQuestion" | "questionCount" | "setQuestionCount" | "attemptedQuestions" | "setAttemptedQuestions" | "score" | "setScore" | "timeLeft" | "setTimeLeft" | "feedback" | "setFeedback" | "generateQuestion" | "quizEnded" | "setQuizEnded" | "resetQuiz">;

export interface IntervalQuizProps {
    availableIntervals: { name: string; semitones: number }[];
    mode: "questions" | "time" | "endless";
    questionLimit?: number;
    timeLimitSeconds?: number;
    playbackOptions: PlaybackOptions;
    currentQuestion?: {
        note1: Note;
        note2: Note;
        correctAnswer: string;
        questionOptions: string[];
    } | null;
    setCurrentQuestion?: (question: any) => void;
    questionCount?: number;
    setQuestionCount?: (count: number) => void;
    attemptedQuestions?: number;
    setAttemptedQuestions?: (count: number) => void;
    score?: number;
    setScore?: (score: number) => void;
    timeLeft?: number;
    setTimeLeft?: (time: number) => void;
    feedback?: { message: string; color: string } | null;
    setFeedback?: (feedback: any) => void;
    generateQuestion?: () => void;
    quizEnded?: boolean;
    setQuizEnded?: (ended: boolean) => void;
    resetQuiz?: () => void;
}

export default function IntervalQuiz({
    availableIntervals,
    mode,
    questionLimit,
    timeLimitSeconds,
    playbackOptions,
    currentQuestion,
    setCurrentQuestion,
    questionCount = 0,
    setQuestionCount,
    attemptedQuestions = 0,
    setAttemptedQuestions,
    score = 0,
    setScore,
    timeLeft = 0,
    setTimeLeft,
    feedback,
    setFeedback,
    generateQuestion,
    quizEnded = false,
    setQuizEnded,
    resetQuiz,
}: IntervalQuizProps) {
    const [disabledOptions, setDisabledOptions] = useState<string[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<string | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Clear any pending timeouts when quiz ends
    useEffect(() => {
        if (quizEnded && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [quizEnded]);

    // Reset correctAnswer and disabledOptions when question changes
    useEffect(() => {
        setCorrectAnswer(null);
        setDisabledOptions([]);
    }, [currentQuestion]);

    // Auto-end quiz when timer runs out
    useEffect(() => {
        if (mode === "time" && timeLeft === 0 && !quizEnded && setQuizEnded) {
            setQuizEnded(true);
        }
    }, [mode, timeLeft, quizEnded, setQuizEnded]);

    // Scroll to top when completion screen shows
    useEffect(() => {
        if (quizEnded || (mode === "questions" && questionLimit && questionCount >= questionLimit)) {
            // Scroll all possible containers
            // 1. The quiz wrapper itself
            if (containerRef.current) {
                containerRef.current.scrollTop = 0;
            }
            // 2. Find and scroll all parent scrollable containers
            let element = containerRef.current?.parentElement;
            while (element) {
                const overflow = window.getComputedStyle(element).overflowY;
                if (overflow === 'auto' || overflow === 'scroll') {
                    element.scrollTop = 0;
                }
                element = element.parentElement;
            }
            // 3. Scroll window as final backup
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [quizEnded, mode, questionLimit, questionCount]);


    const handleAnswer = (answer: string) => {
        if (!currentQuestion || !setFeedback || !setScore || !setQuestionCount || !setCurrentQuestion) return;

        // Increment attempted questions only on the first answer attempt
        if (disabledOptions.length === 0 && setAttemptedQuestions) {
            setAttemptedQuestions(attemptedQuestions + 1);
        }

        if (answer === currentQuestion.correctAnswer) {
            if (disabledOptions.length === 0) {
                setScore(score + 1);
            }
            
            // Increment question count immediately when answer is selected
            setQuestionCount(questionCount + 1);
            
            // ✅ Correct feedback
            setFeedback({ message: `${answer} is correct!`, color: "green" });
            setCorrectAnswer(answer);

            // Check if quiz should end after this question
            const shouldEndQuiz = (mode === "questions" && questionLimit && (questionCount + 1) >= questionLimit) || 
                                 quizEnded;

            // Advance after 2s, but only if quiz isn't ending
            timeoutRef.current = setTimeout(() => {
                setFeedback(null);
                setDisabledOptions([]);
                setCorrectAnswer(null);
                if (!shouldEndQuiz && generateQuestion && !quizEnded) {
                    // Generate new question
                    generateQuestion();
                } else if (shouldEndQuiz && !quizEnded) {
                    // End the quiz when the final question is answered
                    if (setQuizEnded) {
                        setQuizEnded(true);
                    }
                }
                timeoutRef.current = null;
            }, 2000);
        } else {
            // ❌ Wrong feedback
            setDisabledOptions((prev) => [...prev, answer]);
            setFeedback({ message: "Try again!", color: "red" });
        }
    };







    // Show completion screen if quiz ended manually OR question limit reached
    if (quizEnded || (mode === "questions" && questionLimit && questionCount >= questionLimit)) {
        const percentage = attemptedQuestions > 0 ? Math.round((score / attemptedQuestions) * 100) : 0;
        const needsMorePractice = percentage < 70;

        return (
            <div className={styles.quizCompletedPanel}>
                <div className={styles.completionIcon}>🎉</div>
                <h2 className={styles.completionTitle}>Quiz Complete!</h2>
                <div className={styles.scoreDisplay}>
                    <div className={styles.scoreNumber}>{score}/{attemptedQuestions}</div>
                    <div className={styles.scorePercentage}>{percentage}%</div>
                </div>
                <div className={styles.completionMessage}>
                    You answered {score} of {attemptedQuestions} questions correctly
                </div>
                {resetQuiz && (
                    <button onClick={resetQuiz} className={styles.newQuizButton}>
                        New Quiz
                    </button>
                )}
                <div className={styles.completionLearnMore}>
                    <LearnMoreBanner
                        to={ROUTES.LEARN.INTERVALS}
                        variant="completion"
                        title={needsMorePractice ? "Want to improve?" : "Keep learning"}
                        description={needsMorePractice
                            ? "Learn tips for recognizing intervals"
                            : "Discover practice strategies"
                        }
                    />
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={styles.intervalsQuizWrapper}>
            <div className={styles.answerOptionsPanel}>
                {currentQuestion && (
                    <div className={styles.questionOptsWrapper}>
                        {currentQuestion.questionOptions.map((opt, idx) => (
                            <button
                                key={`${currentQuestion.note1}-${currentQuestion.note2}-${idx}`}
                                onClick={() => handleAnswer(opt)}
                                disabled={correctAnswer !== null || disabledOptions.includes(opt)}
                                className={
                                    correctAnswer === opt
                                        ? `${styles.intervalOptButton} ${styles.intervalOptButtonCorrect}`
                                        : disabledOptions.includes(opt)
                                        ? `${styles.intervalOptButton} ${styles.intervalOptButtonDisabled}`
                                        : styles.intervalOptButton
                                }
                            >
                                {opt}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className={styles.feedbackContainer}>
                {feedback && (
                    <span className={feedback.color === "red" ? styles.badFeedback : styles.goodFeedback}>
                        {feedback.message}
                    </span>
                )}
            </div>
        </div>
    );
}
